import React, { useState } from 'react';
import { UcatHeader } from './UcatHeader';
import { UcatFooter } from './UcatFooter';
import { Sparkles, Calculator as LucideCalculator, Flag, Clock, FileText, FormInput, AlignVerticalSpaceAround, AlignVerticalJustifyCenter } from 'lucide-react';
import { Calculator } from '../Calculator/Calculator';
import '../../ucat-styles.css';

interface UcatLayoutProps {
  children: React.ReactNode;
  headerTitle: string;
  headerSubtitle?: React.ReactNode;
  showToolbar?: boolean;
  headerLeftActions?: React.ReactNode;
  headerRightActions?: React.ReactNode;
  toolbarLeftActions?: React.ReactNode;
  toolbarRightActions?: React.ReactNode;
  showExplainAnswerButton?: boolean;
  showCalculatorButton?: boolean;
  showClearFlagButton?: boolean;
  onExplainAnswerClick?: () => void;
  onCalculatorClick?: () => void;
  onClearFlagClick?: () => void;
  timerValue?: string;
  timerLow?: boolean;
  footerProps?: {
    onEndExam?: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    onNavigator?: () => void;
    onReview?: () => void;
    showNavigator?: boolean;
    showPrevious?: boolean;
    showReview?: boolean;
    nextDisabled?: boolean;
    previousDisabled?: boolean;
    leftButtonText?: string;
    rightButtonText?: string;
    reviewButtonText?: string;
    customRightActions?: React.ReactNode;
  };
}

export const UcatLayout: React.FC<UcatLayoutProps> = ({
  children,
  headerTitle,
  headerSubtitle,
  showToolbar = false,
  headerLeftActions,
  headerRightActions,
  toolbarLeftActions,
  toolbarRightActions,
  showExplainAnswerButton = false,
  showCalculatorButton = false,
  showClearFlagButton = false,
  onExplainAnswerClick,
  onCalculatorClick,
  onClearFlagClick,
  timerValue,
  timerLow,
  footerProps
}) => {
  const [internalCalculatorOpen, setInternalCalculatorOpen] = useState(false);

  const handleCalculatorClick = () => {
    if (onCalculatorClick) {
      onCalculatorClick();
    } else {
      setInternalCalculatorOpen(true);
    }
  };

  // Content for the top-right part of the header (Timer and Counter)
  const timerContent = (timerValue || headerSubtitle) ? (
    <>
      {timerValue && (
        <div className={`flex items-center gap-1 text-[16px] mt-[-2px] hover-yellow transition-colors cursor-default ${timerLow ? 'text-[#FFFF00]' : 'text-white'}`}>
          <Clock size={16} strokeWidth={2.5} className="mb-0.5" />
          <span className='text-[16px] f-tahoma'>Time Remaining {timerValue}</span>
        </div>
      )}
      {headerSubtitle && (
        <div className="text-[16px] mt-[2px] font-[400] text-[white] flex items-center justify-end gap-1 hover-yellow transition-colors cursor-default f-tahoma">
          {typeof headerSubtitle === 'string' && headerSubtitle.trim() !== "" && <AlignVerticalSpaceAround size={18} strokeWidth={2.5} className="mb-0.5" />}
          {headerSubtitle}
        </div>
      )}
    </>
  ) : undefined;

  // Left actions for toolbar (like Explain Answer, Calculator)
  const leftToolbarActions = (
    <div className="flex items-center h-full">
      {showExplainAnswerButton && (
        <button
          onClick={onExplainAnswerClick}
          className="flex items-center gap-1 text-white hover:bg-white/10 px-2 h-full transition-colors"
        >
          <Sparkles size={18} className="text-white/90" />
          <span className="text-[16px] underline decoration-1 underline-offset-4 f-tahoma">
            Explain Answer
          </span>
        </button>
      )}
      {showCalculatorButton && (
        <button
          onClick={handleCalculatorClick}
          className="flex items-center gap-1 text-white hover:bg-white/10 px-2 h-full transition-colors"
        >
          <LucideCalculator size={18} className="text-white/90" />
          <span className="text-[16px] font-normal">
            <span className="underline decoration-1 underline-offset-2" style={{ fontSize: '17px' }}>C</span>alculator
          </span>
        </button>
      )}
      {toolbarLeftActions}
    </div>
  );

  // Right actions for toolbar (like Flag)
  const rightToolbarActions = (
    <div className="flex items-center h-full gap-2 pr-2">
      {toolbarRightActions}
    </div>
  );

  return (
    <div className="ucat-module flex flex-col h-screen bg-[#F5F5F5] font-inter overflow-hidden">
      {/* Header */}
      <UcatHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        showToolbar={showToolbar}
        headerRight={timerContent}
        headerRightActions={headerRightActions}
        leftActions={leftToolbarActions}
        rightActions={rightToolbarActions}
      />

      {/* Content */}
      <div className="flex-1 overflow-hidden relative bg-white min-h-0 flex flex-col">
        {children}
      </div>

      {/* Footer */}
      {footerProps && <UcatFooter {...footerProps} />}

      {/* Global Calculator Modal */}
      <Calculator
        isOpen={internalCalculatorOpen}
        onClose={() => setInternalCalculatorOpen(false)}
      />
    </div>
  );
};
